/**
 * workflow-mapper.ts
 *
 * Lossless JSON ↔ IBuilderModel mapper for workflow-builder-v2.
 *
 * Round-trip contract: toJson(fromJson(x)) deep-equals x for any valid
 * workflow JSON (both occupancy-permit PROD and demo fixtures).
 *
 * Strategy: each node (transition, endState, action) carries a `raw` clone
 * of its original JSON node.  When writing back, we start from the raw clone
 * and overlay only the fields the UI can mutate.  Unknown / UI-invisible
 * fields (stateNames, position, eventNames, eventConfigs, endStateCondition
 * extra keys, singular vs plural breakingAction, etc.) survive untouched.
 */

import {
  IBuilderModel,
  ITransition,
  IEndState,
  IAction,
  IStateMeta,
} from '../models/workflow-builder.model';

// ─── helpers ────────────────────────────────────────────────────────────────

let _seq = 0;
const nextId = () => `t${++_seq}`;

/** Deep-clone any JSON-serialisable value. Returns the value itself for null/undefined. */
const clone = <T>(v: T): T => (v == null ? v : JSON.parse(JSON.stringify(v)));

function localizedNameFrom(raw: any, fallback: string): string {
  const names = raw?.stateNames;
  if (!names || typeof names !== 'object' || Array.isArray(names)) {
    return String(raw?.stateName || fallback);
  }
  const preferred =
    names['en-US'] ||
    names['en'] ||
    names['fr-FR'] ||
    names['fr'] ||
    names['rw-RW'] ||
    names['rw'] ||
    Object.values(names).find(
      value => typeof value === 'string' && value.trim()
    );
  return String(preferred || raw?.stateName || fallback);
}

// ─── fromJson helpers ────────────────────────────────────────────────────────

/** Flatten breakingAction / breakingActions / nonBreakingActions into IAction[]. */
function actionsFrom(node: any): IAction[] {
  const out: IAction[] = [];

  // Singular breaking action
  if (node?.breakingAction != null) {
    out.push({
      actionType: node.breakingAction.actionType,
      required: true,
      args: node.breakingAction.args ?? null,
      raw: clone(node.breakingAction),
    });
  }

  // Plural breaking actions
  (node?.breakingActions ?? []).forEach((a: any) =>
    out.push({
      actionType: a.actionType,
      required: true,
      args: a.args ?? null,
      raw: clone(a),
    })
  );

  // Non-breaking actions
  (node?.nonBreakingActions ?? []).forEach((a: any) =>
    out.push({
      actionType: a.actionType,
      required: false,
      args: a.args ?? null,
      raw: clone(a),
    })
  );

  return out;
}

/** Map a raw endState JSON node to IEndState (or null). */
export function endStateFrom(node: any): IEndState | null {
  if (node == null) return null;
  return {
    stateCode: node.stateCode,
    actions: actionsFrom(node),
    nextEvent: node.nextEvent ?? null,
    raw: clone(node),
  };
}

// ─── fromJson ────────────────────────────────────────────────────────────────

export function fromJson(json: any): IBuilderModel {
  // Stash a clone of the original top-level object (minus workflow array)
  // so toJson can reconstruct the full document including nextSteps, etc.
  const { workflow: _wfArr, ...topRestClone } = clone(json);

  const workflow: any[] = json.workflow ?? [];

  const transitions: ITransition[] = workflow.map((t: any) => {
    // Guard clauses live in endStateCondition.endStateTwo
    const esc = t.endStateCondition;
    let guard: ITransition['guard'] = null;
    if (
      esc?.endStateTwo &&
      Array.isArray(esc.endStateTwo) &&
      esc.endStateTwo.length > 0
    ) {
      guard = {
        clauses: esc.endStateTwo.map((c: any) => ({
          parameter: c.parameter,
          comparator: c.comparator,
          value: c.value,
        })),
      };
    }

    return {
      id: nextId(),
      startState: t.startState,
      event: t.event,
      eventNames: t.eventNames ?? null,
      authorisation: t.authorisation ?? null,
      one: endStateFrom(t.endStateOne)!,
      two: endStateFrom(t.endStateTwo),
      guard,
      raw: clone(t),
    };
  });

  const model: IBuilderModel = {
    transitions,
    states: deriveStates(transitions),
    topRaw: topRestClone,
  };

  return model;
}

// ─── toJson ──────────────────────────────────────────────────────────────────

/**
 * Determine whether any IAction in the list has been edited relative to its raw.
 * We detect edits by comparing the current action fields against the raw clone.
 */
function actionsEdited(actions: IAction[], raw: any): boolean {
  const origBreaking: any[] = [];
  if (raw?.breakingAction != null) origBreaking.push(raw.breakingAction);
  (raw?.breakingActions ?? []).forEach((a: any) => origBreaking.push(a));
  const origNonBreaking: any[] = raw?.nonBreakingActions ?? [];

  const origRequired = [
    ...origBreaking.map(() => true),
    ...origNonBreaking.map(() => false),
  ];

  if (actions.length !== origRequired.length) return true;

  for (let i = 0; i < actions.length; i++) {
    if (actions[i].required !== origRequired[i]) return true;
    // Fix 2: only compare actionType against raw when raw is present; the old form
    // `x !== (raw?.x ?? x)` is a tautology when raw is absent.
    if (
      actions[i].raw != null &&
      actions[i].actionType !== (actions[i].raw as any).actionType
    )
      return true;
    // Fix 1: detect args mutations
    if (
      JSON.stringify(actions[i].args) !==
      JSON.stringify((actions[i].raw as any)?.args ?? actions[i].args)
    )
      return true;
  }
  return false;
}

/** Rebuild an endState JSON node from the IEndState model. */
export function endStateToJson(es: IEndState): any {
  const raw: any = es.raw ?? {};

  // If actions were NOT edited: emit raw as-is (preserves singular/plural form,
  // stateNames, position, stateName, etc.)
  if (!actionsEdited(es.actions, raw)) {
    // Overlay only stateCode and nextEvent (these equal raw anyway for unedited case,
    // but we overlay for correctness if someone changed them without touching actions).
    return {
      ...clone(raw),
      stateCode: es.stateCode,
      nextEvent: es.nextEvent,
    };
  }

  // Actions WERE edited — recompute breaking/nonBreaking arrays.
  // We use the raw form key names (singular vs plural) from the original to decide
  // which form to emit for breaking; but since content changed, we must rewrite.
  const breaking = es.actions.filter(a => a.required);
  const nonBreaking = es.actions.filter(a => !a.required);

  // Rebuild action nodes from raw (to preserve all args), with updated actionType/args
  const breakingNodes = breaking.map(a => {
    const base: any = clone(
      a.raw ?? { actionType: a.actionType, args: a.args }
    );
    base.actionType = a.actionType;
    if (a.args !== undefined) base.args = a.args;
    return base;
  });
  const nonBreakingNodes = nonBreaking.map(a => {
    const base: any = clone(
      a.raw ?? { actionType: a.actionType, args: a.args }
    );
    base.actionType = a.actionType;
    if (a.args !== undefined) base.args = a.args;
    return base;
  });

  // Decide singular vs plural for breaking actions: if originally singular
  // and still exactly 1 breaking action, keep singular form.
  const hadSingular =
    raw.breakingAction != null &&
    (raw.breakingActions == null ||
      (Array.isArray(raw.breakingActions) && raw.breakingActions.length === 0));

  let breakingAction: any = null;
  let breakingActions: any[] | null = null;

  if (breakingNodes.length === 0) {
    // Fix 3: all breaking actions were removed — emit null for both forms,
    // never re-emit the original non-empty array.
    breakingAction = null;
    breakingActions = null;
  } else if (hadSingular && breakingNodes.length === 1) {
    breakingAction = breakingNodes[0];
    breakingActions = null;
  } else {
    breakingAction = null;
    breakingActions = breakingNodes;
  }

  return {
    ...clone(raw),
    stateCode: es.stateCode,
    nextEvent: es.nextEvent,
    breakingAction,
    breakingActions,
    nonBreakingActions: nonBreakingNodes.length > 0 ? nonBreakingNodes : null,
  };
}

/**
 * Serialise an IBuilderModel back to the workflow JSON format.
 *
 * For unedited nodes the output is byte-equal to the original (raw overlay).
 * For edited nodes only the mutated fields are rewritten; all other fields
 * (stateNames, position, eventConfigs, endStateCondition extra keys, etc.)
 * are preserved from the raw clone.
 */
/**
 * Serialise ONE transition to its full workflow JSON item (startState, event,
 * authorisation, eventNames, endStateOne/Two, endStateCondition, + preserved raw keys).
 * Reused by `toJson` and by the per-transition JSON editor in the state view.
 */
export function transitionToJson(t: ITransition): any {
  const raw: any = t.raw ?? {};

  const endStateOne = endStateToJson(t.one);
  const endStateTwo =
    t.two != null ? endStateToJson(t.two) : raw.endStateTwo ?? null;

  // Rebuild endStateCondition: update endStateTwo clauses from guard,
  // but preserve all other keys (e.g. extra keys beyond endStateTwo).
  let endStateCondition: any = clone(raw.endStateCondition ?? null);
  if (t.guard != null) {
    if (endStateCondition == null) endStateCondition = {};
    endStateCondition.endStateTwo = t.guard.clauses.map(c => ({
      parameter: c.parameter,
      value: c.value,
      comparator: c.comparator,
    }));
  } else if (
    endStateCondition != null &&
    endStateCondition.endStateTwo != null
  ) {
    // Guard was cleared: remove it
    endStateCondition.endStateTwo = null;
  }

  return {
    ...clone(raw),
    startState: t.startState,
    event: t.event,
    endStateOne,
    endStateTwo,
    endStateCondition,
    authorisation: t.authorisation,
    eventNames: t.eventNames,
  };
}

export function toJson(model: IBuilderModel): any {
  // Reconstruct the full original JSON by starting from each transition's raw clone
  // and overlaying only the UI-editable fields.
  const workflow = model.transitions.map(transitionToJson);

  // Preserve all top-level keys beyond `workflow` (nextSteps, expectedProcessingTime, etc.)
  const topRest: any = model.topRaw ?? {};
  return { workflow, ...topRest };
}

// ─── deriveStates ────────────────────────────────────────────────────────────

/**
 * Derive unique IStateMeta[] from transition list.
 *
 * Algorithm:
 * 1. Walk all transitions; register every startState code (name = code initially).
 * 2. For each endState, register/overwrite code → stateName (from raw node).
 *    This means the human-readable name is taken from when a code first appears
 *    as an endState, which is authoritative.
 * 3. Return one IStateMeta per unique code.
 *
 * Includes all startStates (including NEW) and all endState stateCodes.
 * Names are sourced from stateName in the endState raw node.
 * Colour defaults to '#98a2b3'; pass colourMap to override per code.
 */
export function deriveStates(
  transitions: ITransition[],
  colourMap: Record<string, string> = {}
): IStateMeta[] {
  const codeToName = new Map<string, string>();

  for (const t of transitions) {
    // startState: name = stateCode (we don't have a human name for start states
    // unless we see them as an endState elsewhere)
    if (!codeToName.has(t.startState)) {
      codeToName.set(t.startState, t.startState);
    }

    // endStateOne
    const rawOne: any = t.one.raw ?? {};
    codeToName.set(t.one.stateCode, localizedNameFrom(rawOne, t.one.stateCode));

    // endStateTwo
    if (t.two != null) {
      const rawTwo: any = t.two.raw ?? {};
      codeToName.set(
        t.two.stateCode,
        localizedNameFrom(rawTwo, t.two.stateCode)
      );
    }
  }

  // Second pass: overwrite startState names with their endState name if found
  // (a startState's human name is best sourced from when it appears as an endState)
  // Already handled: the loop above sets endState names after startState names,
  // so if a code appears as both, endState name wins.

  return Array.from(codeToName.entries()).map(([code, name]) => ({
    code,
    name,
    colour: colourMap[code] ?? '#98a2b3',
  }));
}
